import React, { useState, useEffect } from 'react';
import { Check, X, Copy, ExternalLink, Smartphone, Cloud, Instagram, MessageCircle, Key, AlertCircle, ChevronRight, ChevronDown, RefreshCw, Save, Upload, Download, Shield, Database, Globe, Bot, Zap, Settings, HelpCircle, CheckCircle, Circle, ArrowRight, User, Clock, History, AlertTriangle, BookOpen, Phone, Mail, Users, FileText, Lock, Unlock, Edit2, Calendar, MapPin, CreditCard, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ConfigHistory {
  timestamp: string;
  user: string;
  action: 'created' | 'updated' | 'deleted' | 'tested' | 'verified';
  oldValue?: string;
  newValue?: string;
  notes?: string;
}

interface TeamMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: 'owner' | 'admin' | 'developer' | 'viewer';
  avatar?: string;
  lastActive: string;
  permissions: string[];
}

interface RecoveryInfo {
  whereToFind: string;
  howToRegenerate: string;
  alternativeContact: string;
  documentation: string;
  videoTutorial?: string;
  estimatedTime: string;
  prerequisites: string[];
  troubleshooting: string[];
}

interface EnvVariable {
  key: string;
  value: string;
  description: string;
  required: boolean;
  example: string;
  category: 'instagram' | 'whatsapp' | 'cloudflare' | 'api' | 'database';
  secured?: boolean;
  verified?: boolean;
  testEndpoint?: string;
  lastModified?: string;
  modifiedBy?: string;
  history?: ConfigHistory[];
  recovery?: RecoveryInfo;
  expiresAt?: string;
  criticalLevel: 'low' | 'medium' | 'high' | 'critical';
  dependencies?: string[];
}

interface ServiceAccount {
  service: string;
  accountEmail: string;
  accountPassword?: string; // Encrypted reference only
  accountId: string;
  twoFactorEnabled: boolean;
  recoveryEmail: string;
  recoveryPhone: string;
  lastAccessed: string;
  accessedBy: string;
  notes: string;
  documents: {
    type: string;
    url: string;
    description: string;
  }[];
}

interface BackupConfig {
  id: string;
  name: string;
  timestamp: string;
  createdBy: string;
  environment: 'production' | 'staging' | 'development';
  variables: EnvVariable[];
  notes: string;
  verified: boolean;
}

export const IntegrationChecklistV2: React.FC = () => {
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const autoSaveTimeoutRef = React.useRef<NodeJS.Timeout>();
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  
  const [currentUser, setCurrentUser] = useState<TeamMember>({
    id: '1',
    name: 'System Admin',
    email: 'admin@carneirosparadiso.com',
    phone: '+55 81 99999-9999',
    role: 'owner',
    lastActive: new Date().toISOString(),
    permissions: ['all']
  });

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([
    {
      id: '1',
      name: 'João Silva',
      email: 'joao@carneirosparadiso.com',
      phone: '+55 81 99999-9999',
      role: 'owner',
      lastActive: new Date().toISOString(),
      permissions: ['all']
    },
    {
      id: '2',
      name: 'Maria Santos',
      email: 'maria@carneirosparadiso.com',
      phone: '+55 81 88888-8888',
      role: 'admin',
      lastActive: new Date().toISOString(),
      permissions: ['read', 'write', 'test']
    },
    {
      id: '3',
      name: 'Tech Support',
      email: 'support@biig.dev',
      phone: '+55 11 97777-7777',
      role: 'developer',
      lastActive: new Date().toISOString(),
      permissions: ['read', 'write', 'test', 'deploy']
    }
  ]);

  const [serviceAccounts, setServiceAccounts] = useState<ServiceAccount[]>([
    {
      service: 'Facebook Business',
      accountEmail: 'business@carneirosparadiso.com',
      accountId: 'carneiros.paradiso',
      twoFactorEnabled: true,
      recoveryEmail: 'joao@carneirosparadiso.com',
      recoveryPhone: '+55 81 99999-9999',
      lastAccessed: '2024-03-15',
      accessedBy: 'João Silva',
      notes: 'Main business account. 2FA via SMS to owner phone.',
      documents: [
        {
          type: 'Setup Guide',
          url: 'https://business.facebook.com/help',
          description: 'Official Facebook Business setup documentation'
        }
      ]
    },
    {
      service: 'Instagram Business',
      accountEmail: 'Same as Facebook',
      accountId: '@carneirosparadiso',
      twoFactorEnabled: true,
      recoveryEmail: 'business@carneirosparadiso.com',
      recoveryPhone: '+55 81 99999-9999',
      lastAccessed: '2024-03-15',
      accessedBy: 'João Silva',
      notes: 'Connected to Facebook Page. Professional account.',
      documents: [
        {
          type: 'API Access',
          url: 'https://developers.facebook.com/apps',
          description: 'App ID: 1234567890'
        }
      ]
    },
    {
      service: 'CloudFlare',
      accountEmail: 'tech@carneirosparadiso.com',
      accountId: '9f2fcf619e8e9758a4b7e95c878dd49c',
      twoFactorEnabled: true,
      recoveryEmail: 'joao@carneirosparadiso.com',
      recoveryPhone: '+55 81 99999-9999',
      lastAccessed: '2024-03-16',
      accessedBy: 'Tech Support',
      notes: 'Workers and Pages deployment. Free tier.',
      documents: [
        {
          type: 'Dashboard',
          url: 'https://dash.cloudflare.com',
          description: 'Main CloudFlare dashboard'
        }
      ]
    }
  ]);

  const [envVars, setEnvVars] = useState<EnvVariable[]>([
    {
      key: 'INSTAGRAM_ACCESS_TOKEN',
      value: '',
      description: 'Token de acesso do Instagram Business - expira em 60 dias',
      required: true,
      example: 'EAABw...',
      category: 'instagram',
      secured: true,
      verified: false,
      criticalLevel: 'critical',
      expiresAt: '2024-05-15',
      lastModified: '2024-03-15 14:30:00',
      modifiedBy: 'João Silva',
      history: [
        {
          timestamp: '2024-03-15 14:30:00',
          user: 'João Silva',
          action: 'updated',
          notes: 'Renewed token for 60 days'
        },
        {
          timestamp: '2024-01-15 10:00:00',
          user: 'Maria Santos',
          action: 'created',
          notes: 'Initial setup'
        }
      ],
      recovery: {
        whereToFind: 'Facebook Developers > Your App > Access Token Tool',
        howToRegenerate: '1. Go to https://developers.facebook.com/tools/explorer\n2. Select your app\n3. Generate new User Token\n4. Select permissions: instagram_basic, instagram_manage_messages\n5. Generate and copy token',
        alternativeContact: 'Tech Support: +55 11 97777-7777',
        documentation: 'https://developers.facebook.com/docs/instagram-api/getting-started',
        videoTutorial: 'https://youtube.com/watch?v=instagram-token-guide',
        estimatedTime: '5 minutes',
        prerequisites: ['Facebook Developer Account', 'Instagram Business Account', 'App created in Facebook Developers'],
        troubleshooting: [
          'Token expired: Regenerate following steps above',
          'Permission denied: Check app review status',
          'Invalid token: Verify app is not in development mode'
        ]
      },
      dependencies: ['INSTAGRAM_BUSINESS_ACCOUNT_ID']
    },
    {
      key: 'INSTAGRAM_BUSINESS_ACCOUNT_ID',
      value: '17841400000000000',
      description: 'ID único da conta business do Instagram',
      required: true,
      example: '17841400000000000',
      category: 'instagram',
      verified: true,
      criticalLevel: 'high',
      lastModified: '2024-01-15 10:00:00',
      modifiedBy: 'Maria Santos',
      history: [
        {
          timestamp: '2024-01-15 10:00:00',
          user: 'Maria Santos',
          action: 'created',
          notes: 'Found via Graph API Explorer'
        }
      ],
      recovery: {
        whereToFind: 'Facebook Graph API Explorer > Get User ID',
        howToRegenerate: '1. Go to Graph API Explorer\n2. Select your app\n3. Request: GET /me/accounts\n4. Find instagram_business_account > id',
        alternativeContact: 'Maria Santos: +55 81 88888-8888',
        documentation: 'https://developers.facebook.com/docs/instagram-api/reference/user',
        estimatedTime: '2 minutes',
        prerequisites: ['Instagram connected to Facebook Page'],
        troubleshooting: [
          'ID not found: Ensure Instagram is connected to Facebook Page',
          'Wrong ID: Verify using correct Facebook Page'
        ]
      }
    },
    {
      key: 'WHATSAPP_PHONE_NUMBER_ID',
      value: '',
      description: 'ID do número de telefone cadastrado no WhatsApp Business',
      required: true,
      example: '1234567890123456',
      category: 'whatsapp',
      secured: false,
      verified: false,
      criticalLevel: 'critical',
      lastModified: '2024-03-10 09:00:00',
      modifiedBy: 'Tech Support',
      recovery: {
        whereToFind: 'WhatsApp Business API > Phone Numbers > Your Number',
        howToRegenerate: '1. Access WhatsApp Business Manager\n2. Go to Phone Numbers section\n3. Copy Phone Number ID\n4. If missing, re-register number',
        alternativeContact: 'WhatsApp Business Support: business-support@whatsapp.com',
        documentation: 'https://developers.facebook.com/docs/whatsapp/cloud-api/get-started',
        estimatedTime: '10 minutes',
        prerequisites: ['WhatsApp Business Account', 'Verified Phone Number'],
        troubleshooting: [
          'Number not verified: Complete SMS/Call verification',
          'ID not showing: Check account status in Business Manager'
        ]
      },
      dependencies: ['WHATSAPP_ACCESS_TOKEN']
    },
    {
      key: 'CLOUDFLARE_ACCOUNT_ID',
      value: '9f2fcf619e8e9758a4b7e95c878dd49c',
      description: 'ID da conta CloudFlare para deployment',
      required: true,
      example: '9f2fcf619e8e9758a4b7e95c878dd49c',
      category: 'cloudflare',
      verified: true,
      criticalLevel: 'medium',
      lastModified: '2024-01-01 12:00:00',
      modifiedBy: 'Tech Support',
      recovery: {
        whereToFind: 'CloudFlare Dashboard > Right sidebar > Account ID',
        howToRegenerate: 'Account ID is permanent. If lost, login to CloudFlare dashboard.',
        alternativeContact: 'CloudFlare Support: support@cloudflare.com',
        documentation: 'https://developers.cloudflare.com/fundamentals/get-started/basic-tasks/find-account-and-zone-ids',
        estimatedTime: '1 minute',
        prerequisites: ['CloudFlare Account'],
        troubleshooting: [
          'Cannot find ID: Check right sidebar in dashboard',
          'Access denied: Verify account permissions'
        ]
      }
    },
    {
      key: 'D1_DATABASE_ID',
      value: 'f7994da0-0049-4965-8508-2633ebafc511',
      description: 'ID do banco de dados D1 no CloudFlare',
      required: true,
      example: 'f7994da0-0049-4965-8508-2633ebafc511',
      category: 'database',
      verified: true,
      criticalLevel: 'critical',
      lastModified: '2024-02-01 15:00:00',
      modifiedBy: 'Tech Support',
      recovery: {
        whereToFind: 'CloudFlare Dashboard > Workers & Pages > D1 > Your Database',
        howToRegenerate: 'Cannot regenerate. If lost:\n1. Create new D1 database\n2. Run migration scripts\n3. Update all references',
        alternativeContact: 'Tech Support: +55 11 97777-7777',
        documentation: 'https://developers.cloudflare.com/d1',
        estimatedTime: '30 minutes (if recreation needed)',
        prerequisites: ['CloudFlare Account', 'D1 enabled'],
        troubleshooting: [
          'Database not found: Check correct CloudFlare account',
          'Access denied: Verify Worker bindings',
          'Data lost: Restore from R2 backup'
        ]
      },
      dependencies: ['CLOUDFLARE_ACCOUNT_ID']
    }
  ]);

  const [backups, setBackups] = useState<BackupConfig[]>([
    {
      id: '1',
      name: 'Production Backup - March 2024',
      timestamp: '2024-03-15 14:30:00',
      createdBy: 'João Silva',
      environment: 'production',
      variables: envVars,
      notes: 'Monthly backup after token renewal',
      verified: true
    }
  ]);

  const [activeTab, setActiveTab] = useState<'config' | 'accounts' | 'team' | 'history' | 'recovery'>('config');
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedVar, setSelectedVar] = useState<EnvVariable | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showCriticalOnly, setShowCriticalOnly] = useState(false);

  // Calculate days until expiration
  const getDaysUntilExpiration = (expiresAt?: string) => {
    if (!expiresAt) return null;
    const expires = new Date(expiresAt);
    const today = new Date();
    const days = Math.floor((expires.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Get critical status color
  const getCriticalColor = (level: string) => {
    switch (level) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  // Update environment variable with audit trail
  const updateEnvVar = (key: string, value: string, notes?: string) => {
    const updated = envVars.map(env => {
      if (env.key === key) {
        const history = env.history || [];
        history.unshift({
          timestamp: new Date().toISOString(),
          user: currentUser.name,
          action: 'updated',
          oldValue: env.value ? '***' : '',
          newValue: value ? '***' : '',
          notes: notes || 'Updated via web interface'
        });
        
        return {
          ...env,
          value,
          verified: false,
          lastModified: new Date().toISOString(),
          modifiedBy: currentUser.name,
          history: history.slice(0, 10) // Keep last 10 entries
        };
      }
      return env;
    });
    
    setEnvVars(updated);
    setUnsavedChanges(true);
    
    // Auto-save to localStorage immediately
    localStorage.setItem('integration-env-vars-v2', JSON.stringify(updated));
    
    // Auto-save to backend after delay
    if (autoSaveEnabled) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      setSaveStatus('idle');
      autoSaveTimeoutRef.current = setTimeout(() => {
        saveToBackend(updated);
      }, 2000); // 2 second delay
    }
  };
  
  // Save to backend (CloudFlare KV or API)
  const saveToBackend = async (data?: EnvVariable[]) => {
    setSaveStatus('saving');
    const dataToSave = data || envVars;
    
    try {
      // Save to CloudFlare KV via API
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/config/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          variables: dataToSave,
          savedBy: currentUser.name,
          timestamp: new Date().toISOString()
        })
      });
      
      if (response.ok) {
        setSaveStatus('saved');
        setUnsavedChanges(false);
        setNotification({ message: 'Configuration saved successfully to CloudFlare!', type: 'success' });
        setTimeout(() => {
          setSaveStatus('idle');
          setNotification(null);
        }, 3000);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Save failed:', error);
      setSaveStatus('error');
      setNotification({ 
        message: 'Failed to save to server, but your changes are saved locally. They will sync when connection is restored.', 
        type: 'error' 
      });
      setTimeout(() => {
        setSaveStatus('idle');
        setNotification(null);
      }, 5000);
    }
  };
  
  // Manual save all
  const saveAll = () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    saveToBackend();
  };

  // Create backup
  const createBackup = (name?: string) => {
    const backup: BackupConfig = {
      id: Date.now().toString(),
      name: name || `Backup - ${new Date().toLocaleDateString()}`,
      timestamp: new Date().toISOString(),
      createdBy: currentUser.name,
      environment: 'production',
      variables: envVars,
      notes: '',
      verified: false
    };
    
    const updatedBackups = [backup, ...backups].slice(0, 10); // Keep last 10 backups
    setBackups(updatedBackups);
    localStorage.setItem('integration-backups', JSON.stringify(updatedBackups));
    
    // Download backup file
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backup-${Date.now()}.json`;
    a.click();
  };

  // Restore from backup
  const restoreBackup = (backupId: string) => {
    const backup = backups.find(b => b.id === backupId);
    if (backup && window.confirm('This will replace all current configurations. Continue?')) {
      setEnvVars(backup.variables);
      localStorage.setItem('integration-env-vars-v2', JSON.stringify(backup.variables));
      alert('Configuration restored successfully!');
    }
  };

  // Export team documentation
  const exportDocumentation = () => {
    const doc = {
      generatedAt: new Date().toISOString(),
      generatedBy: currentUser.name,
      serviceAccounts,
      teamMembers,
      environmentVariables: envVars.map(env => ({
        ...env,
        value: env.secured ? '***SECURED***' : env.value
      })),
      backups: backups.map(b => ({ id: b.id, name: b.name, timestamp: b.timestamp })),
      recoveryContacts: teamMembers.filter(m => m.role === 'owner' || m.role === 'admin'),
      criticalServices: envVars.filter(e => e.criticalLevel === 'critical').map(e => ({
        service: e.key,
        recovery: e.recovery,
        lastUpdated: e.lastModified,
        updatedBy: e.modifiedBy
      }))
    };
    
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `disaster-recovery-${Date.now()}.json`;
    a.click();
  };

  // Load configuration from backend on mount
  useEffect(() => {
    const loadFromBackend = async () => {
      try {
        const response = await fetch('https://biig-api.b-9f2.workers.dev/api/config/load');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.variables) {
            setEnvVars(data.variables);
            // Also update localStorage as cache
            localStorage.setItem('integration-env-vars-v2', JSON.stringify(data.variables));
          }
        }
      } catch (error) {
        console.error('Failed to load from backend, using local storage:', error);
        // Fall back to localStorage
        const savedEnvVars = localStorage.getItem('integration-env-vars-v2');
        if (savedEnvVars) {
          setEnvVars(JSON.parse(savedEnvVars));
        }
      }
    };
    
    loadFromBackend();
  }, []);
  
  // Warn user about unsaved changes before leaving
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedChanges]);
  
  const filteredVars = envVars.filter(env => {
    const matchesSearch = env.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          env.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || env.category === filterCategory;
    const matchesCritical = !showCriticalOnly || env.criticalLevel === 'critical' || env.criticalLevel === 'high';
    
    return matchesSearch && matchesCategory && matchesCritical;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 p-4">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in ${
          notification.type === 'success' ? 'bg-green-500 text-white' :
          notification.type === 'error' ? 'bg-red-500 text-white' :
          'bg-blue-500 text-white'
        }`}>
          {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
          {notification.type === 'error' && <AlertCircle className="w-5 h-5" />}
          {notification.type === 'info' && <Info className="w-5 h-5" />}
          <span className="font-medium">{notification.message}</span>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto">
        
        {/* Header with User Info */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  Integration Control Center
                </h1>
                <p className="text-gray-600">Single Source of Truth - Auto-saves to CloudFlare KV</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Help Text */}
              <div className="text-xs text-gray-500 mr-4">
                <div className="flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  <span>Changes save automatically after 2 seconds</span>
                </div>
              </div>
              
              {/* Save Status Indicator */}
              <div className="flex items-center gap-2">
                {saveStatus === 'saving' && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Saving...</span>
                  </div>
                )}
                {saveStatus === 'saved' && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Saved</span>
                  </div>
                )}
                {saveStatus === 'error' && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-lg">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">Save failed (local backup active)</span>
                  </div>
                )}
                {unsavedChanges && saveStatus === 'idle' && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-lg">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">Unsaved changes</span>
                  </div>
                )}
              </div>
              
              {/* Auto-save Toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoSaveEnabled}
                  onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Auto-save</span>
              </label>
              
              {/* Manual Save Button */}
              <button
                onClick={saveAll}
                disabled={!unsavedChanges || saveStatus === 'saving'}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                  unsavedChanges && saveStatus !== 'saving'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-lg' 
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                title={unsavedChanges ? 'Save all changes to server' : 'No unsaved changes'}
              >
                <Save className="w-4 h-4" />
                Save All
              </button>
              
              <div className="text-right">
                <div className="text-sm text-gray-600">Logged in as</div>
                <div className="font-semibold">{currentUser.name}</div>
                <div className="text-xs text-gray-500">{currentUser.role}</div>
              </div>
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Critical Alerts */}
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="font-semibold text-red-600">Critical Alerts</span>
            </div>
            <div className="mt-2 space-y-1">
              {envVars.filter(e => {
                const days = getDaysUntilExpiration(e.expiresAt);
                return days !== null && days < 7;
              }).map(env => (
                <div key={env.key} className="text-sm text-red-600">
                  • {env.key} expires in {getDaysUntilExpiration(env.expiresAt)} days
                </div>
              ))}
              {envVars.filter(e => e.required && !e.value).map(env => (
                <div key={env.key} className="text-sm text-red-600">
                  • {env.key} is required but not configured
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-t-2xl shadow-lg p-4">
          <div className="flex gap-2 overflow-x-auto">
            {[
              { id: 'config', label: 'Configuration', icon: Settings },
              { id: 'accounts', label: 'Service Accounts', icon: Key },
              { id: 'team', label: 'Team', icon: Users },
              { id: 'history', label: 'History', icon: History },
              { id: 'recovery', label: 'Recovery', icon: Shield }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'bg-purple-500 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Configuration Tab */}
        {activeTab === 'config' && (
          <div className="bg-white rounded-b-2xl shadow-lg p-6">
            {/* Filters and Actions */}
            <div className="flex flex-wrap gap-3 mb-6">
              <input
                type="text"
                placeholder="Search configurations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 min-w-[200px] px-4 py-2 border rounded-lg"
              />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 border rounded-lg"
              >
                <option value="all">All Categories</option>
                <option value="instagram">Instagram</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="cloudflare">CloudFlare</option>
                <option value="database">Database</option>
              </select>
              <button
                onClick={() => setShowCriticalOnly(!showCriticalOnly)}
                className={`px-4 py-2 rounded-lg ${
                  showCriticalOnly ? 'bg-red-500 text-white' : 'bg-gray-100'
                }`}
              >
                Critical Only
              </button>
              <button
                onClick={() => createBackup()}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
                title="Download a backup of current configuration"
              >
                <Download className="w-4 h-4" />
                Download Backup
              </button>
              <button
                onClick={saveAll}
                disabled={!unsavedChanges}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                  unsavedChanges 
                    ? 'bg-green-500 text-white hover:bg-green-600' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <Save className="w-4 h-4" />
                Save Changes
              </button>
            </div>

            {/* Environment Variables with Full Details */}
            <div className="space-y-4">
              {filteredVars.map(env => (
                <div key={env.key} className={`border rounded-lg p-4 ${
                  env.verified ? 'border-green-500' : 
                  env.required && !env.value ? 'border-red-500' : 'border-gray-200'
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono font-semibold">{env.key}</span>
                        <span className={`px-2 py-1 rounded text-xs ${getCriticalColor(env.criticalLevel)}`}>
                          {env.criticalLevel}
                        </span>
                        {env.expiresAt && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                            Expires in {getDaysUntilExpiration(env.expiresAt)} days
                          </span>
                        )}
                        {env.verified && <CheckCircle className="w-4 h-4 text-green-500" />}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{env.description}</p>
                      
                      {/* Last Modified Info */}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {env.lastModified ? new Date(env.lastModified).toLocaleString() : 'Never modified'}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {env.modifiedBy || 'System'}
                        </span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => setSelectedVar(env)}
                      className="p-2 text-blue-500 hover:bg-blue-50 rounded"
                    >
                      <Info className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Value Input */}
                  <div className="flex gap-2">
                    <input
                      type={env.secured ? 'password' : 'text'}
                      value={env.value}
                      onChange={(e) => updateEnvVar(env.key, e.target.value)}
                      placeholder={env.example}
                      className="flex-1 px-3 py-2 border rounded-lg font-mono text-sm"
                      title="Changes auto-save after 2 seconds or click 'Save All' for immediate save"
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(env.value || env.example)}
                      className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Dependencies */}
                  {env.dependencies && env.dependencies.length > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      Depends on: {env.dependencies.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Service Accounts Tab */}
        {activeTab === 'accounts' && (
          <div className="bg-white rounded-b-2xl shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Service Accounts & Access</h2>
            <div className="space-y-4">
              {serviceAccounts.map(account => (
                <div key={account.service} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-lg">{account.service}</h3>
                      <div className="mt-2 space-y-1 text-sm">
                        <div><strong>Account:</strong> {account.accountEmail}</div>
                        <div><strong>ID:</strong> {account.accountId}</div>
                        <div><strong>2FA:</strong> {account.twoFactorEnabled ? '✅ Enabled' : '❌ Disabled'}</div>
                        <div><strong>Recovery Email:</strong> {account.recoveryEmail}</div>
                        <div><strong>Recovery Phone:</strong> {account.recoveryPhone}</div>
                        <div><strong>Last Accessed:</strong> {account.lastAccessed} by {account.accessedBy}</div>
                      </div>
                      <div className="mt-3 p-3 bg-yellow-50 rounded">
                        <strong className="text-sm">Notes:</strong>
                        <p className="text-sm mt-1">{account.notes}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {account.documents.map((doc, idx) => (
                        <a
                          key={idx}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-blue-500 hover:underline"
                        >
                          <FileText className="w-4 h-4" />
                          {doc.type}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Team Tab */}
        {activeTab === 'team' && (
          <div className="bg-white rounded-b-2xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Team Members</h2>
              <button
                onClick={() => setShowAddMember(true)}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
              >
                Add Member
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamMembers.map(member => (
                <div key={member.id} className="border rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-semibold">{member.name}</div>
                      <div className="text-sm text-gray-600 capitalize">{member.role}</div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {member.email}
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {member.phone}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      Last active: {new Date(member.lastActive).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-xs text-gray-600 mb-1">Permissions:</div>
                    <div className="flex flex-wrap gap-1">
                      {member.permissions.map(perm => (
                        <span key={perm} className="px-2 py-1 bg-gray-100 rounded text-xs">
                          {perm}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Emergency Contacts */}
            <div className="mt-6 p-4 bg-red-50 rounded-lg">
              <h3 className="font-semibold text-red-700 mb-3">Emergency Contacts</h3>
              <div className="space-y-2">
                {teamMembers
                  .filter(m => m.role === 'owner' || m.role === 'admin')
                  .map(member => (
                    <div key={member.id} className="flex items-center justify-between text-sm">
                      <span>{member.name} ({member.role})</span>
                      <div className="flex gap-4">
                        <span>{member.phone}</span>
                        <span>{member.email}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-b-2xl shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Configuration History</h2>
            <div className="space-y-2">
              {envVars
                .filter(e => e.history && e.history.length > 0)
                .flatMap(e => e.history?.map(h => ({ ...h, key: e.key })) || [])
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                .slice(0, 20)
                .map((entry, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-semibold">{entry.key}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          entry.action === 'created' ? 'bg-green-100 text-green-700' :
                          entry.action === 'updated' ? 'bg-blue-100 text-blue-700' :
                          entry.action === 'deleted' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {entry.action}
                        </span>
                      </div>
                      {entry.notes && (
                        <p className="text-sm text-gray-600 mt-1">{entry.notes}</p>
                      )}
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <div>{entry.user}</div>
                      <div>{new Date(entry.timestamp).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Recovery Tab */}
        {activeTab === 'recovery' && (
          <div className="bg-white rounded-b-2xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Disaster Recovery</h2>
              <button
                onClick={exportDocumentation}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                <Download className="w-4 h-4 inline mr-2" />
                Export Documentation
              </button>
            </div>

            {/* Backups */}
            <div className="mb-6">
              <h3 className="font-semibold mb-3">Available Backups</h3>
              <div className="space-y-2">
                {backups.map(backup => (
                  <div key={backup.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{backup.name}</div>
                      <div className="text-sm text-gray-500">
                        {new Date(backup.timestamp).toLocaleString()} by {backup.createdBy}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => restoreBackup(backup.id)}
                        className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                      >
                        Restore
                      </button>
                      <button
                        onClick={() => {
                          const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${backup.name.replace(/\s+/g, '-')}.json`;
                          a.click();
                        }}
                        className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recovery Instructions */}
            <div className="space-y-4">
              <h3 className="font-semibold">Recovery Instructions by Service</h3>
              {envVars
                .filter(e => e.criticalLevel === 'critical' || e.criticalLevel === 'high')
                .map(env => (
                  <div key={env.key} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono font-semibold">{env.key}</span>
                      <span className={`px-2 py-1 rounded text-xs ${getCriticalColor(env.criticalLevel)}`}>
                        {env.criticalLevel}
                      </span>
                    </div>
                    {env.recovery && (
                      <div className="space-y-3 text-sm">
                        <div>
                          <strong>Where to find:</strong>
                          <p className="mt-1 p-2 bg-gray-50 rounded">{env.recovery.whereToFind}</p>
                        </div>
                        <div>
                          <strong>How to regenerate:</strong>
                          <pre className="mt-1 p-2 bg-gray-50 rounded whitespace-pre-wrap">
                            {env.recovery.howToRegenerate}
                          </pre>
                        </div>
                        <div>
                          <strong>Time required:</strong> {env.recovery.estimatedTime}
                        </div>
                        <div>
                          <strong>Alternative contact:</strong> {env.recovery.alternativeContact}
                        </div>
                        {env.recovery.documentation && (
                          <div>
                            <strong>Documentation:</strong>
                            <a href={env.recovery.documentation} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline ml-2">
                              View Docs
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Selected Variable Details Modal */}
        {selectedVar && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold font-mono">{selectedVar.key}</h3>
                <button
                  onClick={() => setSelectedVar(null)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {selectedVar.recovery && (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Recovery Information</h4>
                    <div className="space-y-2 text-sm">
                      <div><strong>Where to find:</strong> {selectedVar.recovery.whereToFind}</div>
                      <div><strong>Time to regenerate:</strong> {selectedVar.recovery.estimatedTime}</div>
                      <div><strong>Contact:</strong> {selectedVar.recovery.alternativeContact}</div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Step-by-Step Recovery</h4>
                    <pre className="p-3 bg-gray-100 rounded text-sm whitespace-pre-wrap">
                      {selectedVar.recovery.howToRegenerate}
                    </pre>
                  </div>
                  
                  {selectedVar.recovery.troubleshooting && (
                    <div>
                      <h4 className="font-semibold mb-2">Common Issues</h4>
                      <ul className="space-y-1">
                        {selectedVar.recovery.troubleshooting.map((item, idx) => (
                          <li key={idx} className="text-sm">• {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {selectedVar.history && selectedVar.history.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold mb-2">Change History</h4>
                  <div className="space-y-2">
                    {selectedVar.history.map((entry, idx) => (
                      <div key={idx} className="text-sm p-2 bg-gray-50 rounded">
                        <div className="flex justify-between">
                          <span>{entry.action} by {entry.user}</span>
                          <span className="text-gray-500">{new Date(entry.timestamp).toLocaleString()}</span>
                        </div>
                        {entry.notes && <div className="text-gray-600 mt-1">{entry.notes}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};